import { Divider } from '@mui/material';
import FilterComponent from 'lib/common/FilterNonSearch';
import PageHeader, { ToggleTab } from 'lib/designSystem/PageHeader';
import EnhancedTable from 'lib/designSystem/Table';
import { formatDate } from 'lib/functions/utils';
import CreditNoteAlert from 'pages/Finance/Components/Financing/common/documentsSelector/components/CreditNoteAlert';
import React from 'react';

import {
  getDisplayAmount,
  IDocument,
  IDocumentStore,
  PaymentType,
} from '../types';

const TOGGLE_BUTTON_OPTIONS: { value: PaymentType; label: string }[] = [
  { value: 'creditDocuments', label: 'Crédito' },
  { value: 'cashDocuments', label: 'Contado' },
  { value: 'receiptFeeDocuments', label: 'Boletas de honorarios' },
];

const TABS: ToggleTab[] = TOGGLE_BUTTON_OPTIONS.map(({ value, label }) => ({
  id: value,
  label,
  value,
}));

const DOCUMENT_HEADERS = [
  { key: 'folio', label: 'Folio' },
  {
    key: 'creditNotesAmount',
    label: 'NC',
    type: 'custom',
    customComponent: (value: number | string) => (
      <CreditNoteAlert creditNotesValue={Number(value) || 0} />
    ),
  },
  { key: 'businessName', label: 'Emisor' },
  { key: 'businessIdentifier', label: 'Rut' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'amountToPay', label: 'Monto disponible', type: 'money' },
  {
    key: 'issuedDate',
    label: 'Fecha emisión',
    type: 'date',
    formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
  },
];

export interface StepSelectDocumentsProps {
  documents: IDocumentStore;
  filteredDocuments: IDocument[];
  setFilteredDocuments: (docs: IDocument[]) => void;
  paymentType: PaymentType;
  onPaymentTypeChange: (type: PaymentType) => void;
  selectedDocuments: number[];
  onSelectedDocumentsChange: (ids: number[]) => void;
}

const StepSelectDocuments: React.FC<StepSelectDocumentsProps> = ({
  documents,
  filteredDocuments,
  setFilteredDocuments,
  paymentType,
  onPaymentTypeChange,
  selectedDocuments,
  onSelectedDocumentsChange,
}) => {
  const documentsWithDisplayAmount = filteredDocuments.map((doc) => ({
    ...doc,
    displayAmount: getDisplayAmount(doc),
  }));

  const cantSelectRow = (row: IDocument): boolean => {
    if (paymentType === 'cashDocuments') return false;
    const firstSelectedDoc = documents[paymentType].find(
      (doc) => doc.id === selectedDocuments[0],
    );
    if (
      firstSelectedDoc &&
      row.businessIdentifier !== firstSelectedDoc.businessIdentifier
    ) {
      return true;
    }
    return false;
  };

  return (
    <div>
      <PageHeader variant="dialogHeader">
        <PageHeader.Main>
          <PageHeader.TitleSection
            iconType="documents"
            title="Seleccione los documentos a registrar"
          />
          <PageHeader.Filters>
            <FilterComponent
              data={documents[paymentType]}
              filterFields={[
                { key: 'businessName', filterName: 'Emisor' },
                { key: 'folio', filterName: 'Folio' },
              ]}
              setFilteredData={setFilteredDocuments}
            />
          </PageHeader.Filters>
        </PageHeader.Main>
        <Divider sx={{ my: 1 }} />
        <PageHeader.ActionsBar>
          <PageHeader.ToggleGroup
            tabs={TABS}
            value={paymentType}
            onChange={(value) => onPaymentTypeChange(value as PaymentType)}
          />
        </PageHeader.ActionsBar>
      </PageHeader>
      <EnhancedTable
        data={documentsWithDisplayAmount}
        dataHeaders={DOCUMENT_HEADERS}
        selected={selectedDocuments}
        setSelected={onSelectedDocumentsChange}
        rowsPerPageDefault={5}
        cantSelectRow={cantSelectRow}
        exportToFile="xlsx"
      />
    </div>
  );
};

export default StepSelectDocuments;
